<script context="module">
	const expectedNomalStatus = ["OK", "CREATED", "UPDATED", "DELETED"];
</script>

<script lang="ts">
	import { GoBackButton, AutoComplete } from "@hitit/new_ui/src";
	import ContainerLoading from "@hitit/hub/src/v7/components/common/ContainerLoading.svelte";
	import Collection from "@hitit/hub/src/v7/components/product/Collection.svelte";
	import ActionWithCollections from "../components/product/ActionWithCollections.svelte";
	import EditViewer from "../components/product/Option/EditViewer.svelte";
	import SetupCollection from "../components/product/Option/SetupCollection.svelte";
	import OptionCollection from "../class/StoreItem/OptionCollection";
	import DetailViewer from "../components/product/Option/DetailViewer.svelte";
	import AreYouSure from "../components/common/AreYouSure.svelte";
	import MultipleEditViewer from "../components/product/Option/MultipleEditViewer.svelte";
	//Utils
	import { api, notifications, t } from "@hitit/hub/src/stores";
	import { popup, saveStatus } from "../store";
	import { onDestroy } from "svelte";
	import { dataLayerPush, getKeyInTranslation, scrollTopBy } from "../utils";
	import OptionItem from "../class/StoreItem/OptionItem";
	import OptionCategory from "../class/StoreItem/OptionCategory";
	import { scrollBlockRequest } from '../../stores/dom';

	// Types
	import type {
		OptionCategoryData,
		OptionCollectionItemDTO,
		UpdateDisplaysortParam,
	} from "@hitit/api";
	type UpdateDisplaysortEventDetail = {
		oldIndex: number;
		newIndex: number;
	};
	type MultidragIndex = { multiDragElement: HTMLElement; index: number };
	type UpdateMultipleDisplaysortEventDetail = {
		oldIndicies: MultidragIndex[];
		newIndicies: MultidragIndex[];
	};
	//Props
	export let storeId = "";
	export let editable = true;

	// Contstants
	const tempReg = /^\|TEMP_ITEM\|/;
	const symbol = Symbol("forScrollBlock")

	//Variables
	let loading = false;
	let editmode = false;
	let multipleSelectMode = false;
	let multipleSelectEdit = false;
	let product: OptionCollection;
	let setupCategory: OptionCategory | null = null;
	let selectedItems: OptionItem[] = [];
	let selectedCategoryUid = "";

	//Utils
	const mobileSaveHandler = () => {
		if (window.innerWidth < 1024) selectedItems = [];
	};

	//Autocomplete
	const autocompleteCallback = (uid: string, id: string) => {
		selectedCategoryUid = uid;
		setTimeout(() => {
			const item = document.getElementById(`${id}`);
			if (item) {
				item.classList.add("found");
				setTimeout(() => {
					item.classList.remove("found");
				}, 2000);
			}
		}, 20);
	};

	const autocompleteHandler = (search: string) => {
		const items = product.findOption(search);
		return Promise.resolve(
			items.map((v) => {
				const name =
					getKeyInTranslation(v.getTranslation(), "name", "ko") || "";
				return {
					name,
					callback: () => autocompleteCallback(v.uid || "", v.id || ""),
				};
			})
		);
	};

	// ActionWithCollections Handler
	const collectionTextClickHandler = () =>
		(selectedItems = !multipleSelectMode ? [] : selectedItems);

	const multipleEditHandler = () => {
		const condition = !multipleSelectEdit && selectedItems.length === 0;
		multipleSelectEdit = condition ? multipleSelectEdit : !multipleSelectEdit;
	};
	const multipleModeChangeHandler = () => {
		const condition = multipleSelectMode;
		selectedItems = condition
			? []
			: selectedItems.filter((v) => !tempReg.test(v.id || ""));
		multipleSelectMode = !multipleSelectMode;
	};

	// PopupHandler
	const deleteMultipleOptionItemPopupHandler = () => {
		$popup = {
			component: AreYouSure,
			props: {
				textTranslation: "global.product.options.you_want_to_delete",
				accept: () => multipleItemDelete(),
				cancel: () => ($popup = undefined),
			},
		};
	};
	const deleteSingleOptionItemPopupHanlder = (e: { detail: OptionItem }) => {
		const selectedOptionCategory = selectedCategory as OptionCategory;
		$popup = {
			component: AreYouSure,
			props: {
				textTranslation: "global.product.options.you_want_to_delete_item",
				accept: () => optionItemDelete(selectedOptionCategory, e.detail.uid),
				cancel: () => ($popup = undefined),
			},
		};
	};
	const deleteCollectionPopupHandler = (e: { detail: OptionCategory }) => {
		$popup = {
			component: AreYouSure,
			props: {
				textTranslation: "global.product.options.you_want_to_delete",
				accept: () => deleteCollection(e.detail),
				cancel: () => ($popup = undefined),
			},
		};
	};
	const categoryDisplaysortHandler = async ({
		detail,
	}: {
		detail: UpdateDisplaysortEventDetail;
	}) => {
		try {
			$saveStatus.options = true;
			const { oldIndex, newIndex } = detail;
			if (oldIndex === newIndex) return;

			const updatedCategories: UpdateDisplaysortParam =
				product.updateCategoryDisplaysort(oldIndex, newIndex);

			const { data } = await $api.setOptioncollectionDisplaysort(
				storeId,
				updatedCategories
			);

			const notNormalStatus =
				expectedNomalStatus.find((v) => v === data.status) === undefined;
			if (notNormalStatus) throw new Error(data.status);
			dataLayerPush("UPDATE_OPTION");
			product = product;
			//
		} catch (error) {
			errorHandler(error as Error);
		} finally {
			$saveStatus.options = false;
		}
	};

	const updateMultipleOptionItemDisplaySort = async ({
		detail,
	}: {
		detail: UpdateMultipleDisplaysortEventDetail;
	}) => {
		if ($saveStatus.options) return;
		try {
			$saveStatus.options = true;
			const { oldIndicies, newIndicies } = detail;
			const hasNotSelectedCategory = selectedCategory == null;
			const lengthIsZero = oldIndicies.length === 0 || newIndicies.length === 0;
			const oldIndexies = oldIndicies.map((v) => v.index);
			const newIndexies = newIndicies.map((v) => v.index);
			if (hasNotSelectedCategory || lengthIsZero) return;
			const updatedItems = selectedCategory!.updateMultipleDisplaySort(
				oldIndexies,
				newIndexies
			);

			const { data } = await $api.setOptioncollectionItemDisplaysort(
				storeId,
				updatedItems
			);

			const notNormalStatus =
				expectedNomalStatus.find((v) => v === data.status) === undefined;
			if (notNormalStatus) throw new Error(data.status);
			dataLayerPush("UPDATE_OPTIONITEM");
			product = product;
			selectedCategory = selectedCategory;
			selectedItems = selectedItems.map((v) => {
				const category = v.collection;
				return category.findOptionByuid(v.uid || "") as OptionItem;
			});
		} catch (error) {
			errorHandler(error as Error);
		} finally {
			$saveStatus.options = false;
		}
	};

	const itemDisplaysortHandler = async ({
		detail,
	}: {
		detail: UpdateDisplaysortEventDetail;
	}) => {
		try {
			$saveStatus.options = true;
			const { oldIndex, newIndex } = detail;
			if (oldIndex === newIndex) return;
			if (selectedCategory == undefined) return;
			const updatedItems: UpdateDisplaysortParam =
				selectedCategory.updateDisplaysort(oldIndex, newIndex);

			const { data } = await $api.setOptioncollectionItemDisplaysort(
				storeId,
				updatedItems
			);

			const notNormalStatus =
				expectedNomalStatus.find((v) => v === data.status) === undefined;
			if (notNormalStatus) throw new Error(data.status);
			dataLayerPush("UPDATE_OPTIONITEM");
			product = product;
			selectedCategory = selectedCategory;

			//
		} catch (error) {
			errorHandler(error as Error);
		} finally {
			$saveStatus.options = false;
		}
	};

	// Create New Option , Collection
	const addNewCollection = async (e: { detail: string }) => {
		try {
			$saveStatus.options = true;
			const categories = product.getCategories();
			const displaySort =
				categories.length > 0
					? categories[categories.length - 1].displaySort + 1
					: 0;
			const category = OptionCategory.convinienceConstructor(
				e.detail,
				displaySort
			);
			const [uid, , categoryDTO] = await updateCollection(
				category.getOptionCategoryDTO()
			);
			const newCategory = new OptionCategory(categoryDTO);
			product.addNewCategory(newCategory);
			dataLayerPush("CREATE_NEW_OPTION");
			selectedItems = [];
			selectedCategoryUid = uid;
			product = product;
		} catch (e) {
			errorHandler(e as Error);
		} finally {
			$saveStatus.options = false;
		}
	};

	const newOptionItemEvent = (e: { detail: OptionCategory }) => {
		const hasItem = e.detail.getStoreItemLength() >= 1;
		const displaySort = e.detail.getLastDisplaysort();
		const arg = OptionItem.convienceArg(
			hasItem ? displaySort + 1 : 0,
			`|TEMP_ITEM|${Math.random()}`
		);
		selectedItems = [OptionItem.convinienceConstructor(arg, "ko", e.detail)];
	};

	//Collection Handler
	const setupCategoryHandler = (e: { detail: OptionCategory }) =>
		(setupCategory = setupCategory === null ? e.detail : null);
	const onItemClickEvent = (e: { detail: OptionItem }) =>
		(selectedItems = [e.detail]);

	// Single
	const optionItemDelete = async (
		selectedCategory: OptionCategory,
		uid?: string
	) => {
		$popup = undefined;
		try {
			$saveStatus.options = true;
			const dto = selectedCategory.getOptionCategoryDTO();
			const filteredOptionCollection = dto.optionCollectionItemDTO.filter(
				(v) => v.uid !== uid
			);

			dto.optionCollectionItemDTO = filteredOptionCollection;
			const [newUid, , categoryData] = await updateCollection(dto);
			const newCategory = new OptionCategory(categoryData);
			product.replaceCategoryByUid(newUid, newCategory);
			dataLayerPush("DELETE_OPTIONITEM");
			product = product;
			selectedItems = selectedItems.filter((v) => v.uid !== uid);
		} catch (e) {
			errorHandler(e as Error);
		} finally {
			$saveStatus.options = false;
		}
	};

	const optionItemEditHandler = async (e: {
		detail: OptionCollectionItemDTO;
	}) => {
		try {
			$saveStatus.options = true;
			const editItem = e.detail;
			const isNew = editItem.uid === undefined;
			const categoryDTOarg = selectedCategory!.getOptionCategoryDTO();
			const filteredOriginItemDTOs =
				categoryDTOarg.optionCollectionItemDTO.filter(
					(v) => v.uid !== editItem.uid
				);
			const originItem = selectedCategory!
				.getOptions()
				.find((v) => v.uid === editItem.uid);

			const newEditItem = Object.assign({}, editItem, {
				id: originItem?.id,
				displaySort: originItem?.displaySort || editItem.displaySort,
			});

			categoryDTOarg.optionCollectionItemDTO = [
				...filteredOriginItemDTOs,
				newEditItem,
			];

			const [uid, , categoryDTO] = await updateCollection(categoryDTOarg);
			const newCategory = new OptionCategory(categoryDTO);
			product.replaceCategoryByUid(uid, newCategory);

			if (isNew) {
				selectedItems = [];
			} else {
				afterUpdateHandleSelectItems();
			}
			dataLayerPush(isNew ? "CREATE_NEW_OPTIONITEM" : "UPDATE_OPTIONITEM");
			product = product;
			mobileSaveHandler();
		} catch (e) {
			errorHandler(e as Error);
		} finally {
			$saveStatus.options = false;
		}
	};

	const updateCollection = async (optionCategory: OptionCategoryData) => {
		try {
			const setOptionCollectionResponse = await $api.setOptionCollection(
				storeId,
				optionCategory
			);
			const getOptionCollectionsResponse = await $api.getStoreOptionCollection(
				storeId
			);
			const { uid, id, status } = setOptionCollectionResponse.data;
			const newCategoryData =
				getOptionCollectionsResponse.data.optionCollectionDTOs.find(
					(v) => v.id === id
				);
			const throwErrorCondition =
				status !== "OK" || newCategoryData === undefined;
			if (throwErrorCondition) throw new Error(status);

			return [uid, id, newCategoryData] as [string, string, OptionCategoryData];
		} catch (e) {
			throw e;
		}
	};

	const setupCollectionUpdateHandler = async (e: {
		detail: OptionCategoryData;
	}) => {
		try {
			$saveStatus.options = true;
			//
			const dto = e.detail;
			const originUid = dto.uid || "";
			const [, , categoryDTO] = await updateCollection(dto);
			const newCategory = new OptionCategory(categoryDTO);
			product.replaceCategoryByUid(originUid, newCategory);
			dataLayerPush("UPDATE_OPTION");
			product = product;
		} catch (e) {
			errorHandler(e as Error);
		} finally {
			$saveStatus.options = false;
		}
	};
	// Multiple
	const multipleViewerSaveHandler = async (e: { detail: OptionItem[] }) => {
		try {
			$saveStatus.options = true;
			const toBeModifiedCollecitons = [
				...new Set(selectedItems.map((v) => v.collection)),
			].map((v) => v.getOptionCategoryDTO());
			for (const item of e.detail) {
				const collectionUid = item.collection.uid;
				const find = toBeModifiedCollecitons.find(
					(v) => v.uid === collectionUid
				);

				if (find) {
					const filteredOptionItems = find.optionCollectionItemDTO.filter(
						(v) => v.uid !== item.uid
					);
					find.optionCollectionItemDTO = [
						...filteredOptionItems,
						item.getOptionCollectionItemDTO(),
					];
				}
			}

			for (const categoryData of toBeModifiedCollecitons) {
				const [uid, , categoryDTO] = await updateCollection(categoryData);
				const newCategory = new OptionCategory(Object.assign(categoryDTO));
				product.replaceCategoryByUid(uid || "", newCategory);
			}
			dataLayerPush("UPDATE_OPTIONITEM");
			product = product;
			afterUpdateHandleSelectItems();
		} catch (e) {
			errorHandler(e as Error);
		} finally {
			mobileSaveHandler();
			$saveStatus.options = false;
			product = product;
		}
	};

	const multipleItemDelete = async () => {
		try {
			$saveStatus.options = true;
			const toBeModifiedCollecitons = [
				...new Set(selectedItems.map((v) => v.collection)),
			].map((v) => v.getOptionCategoryDTO());

			for (const item of selectedItems) {
				const collectionUid = item.collection.uid;
				const find = toBeModifiedCollecitons.find(
					(v) => v.uid === collectionUid
				);

				if (find) {
					const filteredOptionItems = find.optionCollectionItemDTO.filter(
						(v) => v.uid !== item.uid
					);
					find.optionCollectionItemDTO = filteredOptionItems;
				}
			}
			dataLayerPush("DELETE_OPTIONITEM");
			for (const categoryData of toBeModifiedCollecitons) {
				const [uid, , categoryDTO] = await updateCollection(categoryData);
				const newCategory = new OptionCategory(Object.assign(categoryDTO));
				product.replaceCategoryByUid(uid || "", newCategory);
			}
			selectedItems = [];
			product = product;
		} catch (e) {
			errorHandler(e as Error);
		} finally {
			$saveStatus.options = false;
			$popup = undefined;
		}
	};
	//DeleteCollection.
	const deleteCollection = async (target: OptionCategory) => {
		try {
			$popup = undefined;
			$saveStatus.options = true;
			const { status } = (
				await $api.deleteStoreItemOptionCollection(target.uid as string)
			).data;
			const throwErrorCondition =
				expectedNomalStatus.find((v) => status === v) === undefined;
			if (throwErrorCondition) throw new Error(status);
			product.deleteCategory(target);
			dataLayerPush("DELETE_OPTION");
			selectedItems = [];
			product = product;
		} catch (e) {
			errorHandler(e as Error);
		} finally {
			$saveStatus.options = false;
		}
	};

	// MultipleEditViewer
	const multipleViewerDeleteHandler = ({ detail }: { detail: string }) => {
		selectedItems = selectedItems.filter((v) => v.id !== detail);
	};

	//self
	const afterUpdateHandleSelectItems = () => {
		const newSelectedItems = selectedItems
			.map((v) => {
				if (tempReg.test(v.id || "")) {
					const parent = product.findOptionByUid(v.collection.uid || "");
					v.collection = parent as OptionCategory;
					return v;
				}
				const parent = product.findOptionByUid(v.collection.uid || "");
				const option = parent?.findOptionByuid(v.uid || "");
				return option;
			})
			.filter((v) => v !== undefined) as OptionItem[];

		selectedItems = newSelectedItems;
	};

	const errorHandler = async (error: Error) => {
		const message = error.message;
		const errorTranslation = $t(`error.DATA_WAS_BEEN_MODIFIED`);
		const errorMessage = `
			ERROR STATUS : ${message},<br>
			${errorTranslation}
		`;
		notifications.append("ERROR", errorMessage, "", undefined, "ERROR");
		console.log(error);
		await getStoreOptions(storeId);
	};

	async function getStoreOptions(storeId: string) {
		try {
			loading = true;
			const { data } = await $api.getStoreOptionCollection(storeId);
			const { optionCollectionDTOs, revision } = data;

			product = new OptionCollection(optionCollectionDTOs, revision);
			selectedCategoryUid = product
				.getNameIdItemLength()
				.reduce((prev, cur, i) => {
					if (i === 0) return cur.uid || "";

					return prev;
				}, "");
			selectedItems = [];
		} catch (e) {
			console.log(e);
		} finally {
			loading = false;
		}
	}

	$: selectedCategory = product
		? product.getCategories().find((v) => v.uid === selectedCategoryUid)
		: null;
	$: detailDisplayCondition =
		(!editmode && selectedItems.length === 1) ||
		(editmode && !multipleSelectMode && selectedItems.length === 1) ||
		(editmode &&
			multipleSelectMode &&
			selectedItems.length >= 1 &&
			multipleSelectEdit);
	$: detailDisplayCondition ? scrollBlockRequest.blockRequest(symbol) : scrollBlockRequest.removeRequest(symbol);
	$: getStoreOptions(storeId);

	onDestroy(() => {
		$popup = undefined;
		scrollBlockRequest.removeRequest(symbol)
	});
</script>

<div class="container">
	{#if loading}
		<ContainerLoading />
	{/if}
	{#if product}
		<form
			id="search-item"
			on:submit|preventDefault
			class:mobileHide={detailDisplayCondition}
		>
			<AutoComplete
				placeholder={$t("global.product.options.find_option")}
				searchCallback={autocompleteHandler}
			/>
		</form>
		<div class="actions" class:mobileHide={detailDisplayCondition}>
			<ActionWithCollections
				placeholder={$t("global.product.options.option_name")}
				on:EDIT_CHANGE={() => (editmode = !editmode)}
				on:MULTIPLE_DELETE={deleteMultipleOptionItemPopupHandler}
				on:MULTIPLE_SELECT_MODE_CHANGE={multipleModeChangeHandler}
				on:MULTIPLE_EDIT={multipleEditHandler}
				on:CLICK_COLLECTION_TEXT={collectionTextClickHandler}
				on:NEW_COLLECTION={addNewCollection}
				on:UPDATED_COLLECTION_DISPLAY_SORT={categoryDisplaysortHandler}
				bind:editmode={editmode}
				bind:multipleSelectMode={multipleSelectMode}
				bind:selectedCollectionUid={selectedCategoryUid}
				sortDisable={editmode === false}
				editable={editable}
				collections={product.getNameIdItemLength()}
			/>
		</div>
		<div class="list-detail-wrapper">
			<div class="list" class:mobileHide={detailDisplayCondition}>
				{#if selectedCategory}
					<Collection
						on:COLLECTION_SETUP_EVENT={setupCategoryHandler}
						on:ITEM_CLICK_EVENT={onItemClickEvent}
						on:ITEM_DELETE_CLICK_EVENT={deleteSingleOptionItemPopupHanlder}
						on:NEW_ITEM_EVENT={newOptionItemEvent}
						on:UPDATED_ITEM_DISPLAY_SORT={itemDisplaysortHandler}
						on:UPDATED_MULTIPLE_ITEM_DISPLAY_SORT={updateMultipleOptionItemDisplaySort}
						bind:selectedItems={selectedItems}
						hasSetup={Boolean(setupCategory)}
						collection={selectedCategory}
						items={selectedCategory?.getOptions()}
						sortDisable={editmode === false}
						editmode={editmode}
						multipleSelectMode={multipleSelectMode}
					>
						<div slot="setup">
							{#if setupCategory}
								<SetupCollection
									on:UPDATE_COLLECTION={setupCollectionUpdateHandler}
									on:DELETE_OPTION_CATEGORY={deleteCollectionPopupHandler}
									bind:optionCategory={selectedCategory}
									disabled={$saveStatus.options}
									clickOutsideCallBack={() => (setupCategory = null)}
								/>
							{/if}
						</div>
					</Collection>
				{/if}
			</div>
			<div
				class="detail"
				use:scrollTopBy={detailDisplayCondition}
				class:mobileDisplayCondition={detailDisplayCondition}
			>
				<div class="goBack">
					<GoBackButton
						on:click={() => {
							selectedItems = [];
							multipleSelectEdit = false;
						}}
					/>
				</div>
				{#if !editmode && selectedItems.length === 1}
					<DetailViewer item={selectedItems[0]} />
				{:else if editmode && !multipleSelectMode && selectedItems.length === 1}
					<EditViewer
						on:EDIT_ITEM_DONE={optionItemEditHandler}
						item={selectedItems[0].getClone()}
					/>
				{:else if editmode && multipleSelectMode && selectedItems.length >= 1}
					<MultipleEditViewer
						on:UPDATE_MULTIPLE_ITEMS={multipleViewerSaveHandler}
						on:MULTIPLE_SELECTED_OPTION_DELETE={multipleViewerDeleteHandler}
						items={selectedItems.map((v) => v.getClone())}
					/>
				{/if}
			</div>
		</div>
	{/if}
</div>

<style lang="scss">
	@import "../../variables.scss";

	.container {
		display: flex;
		flex-direction: column;
		width: 100%;
		gap: 22px;

		#search-item {
			width: 100%;
			height: 31px;
			color: #46474b;
		}

		.mobileHide {
			display: none;
		}
		.detail {
			display: none;
			position: fixed;
			top: 0;
			left: 0;
			right: 0;
			min-height: 100vh;
			max-height: 100vh;
			overflow-y: auto;
			box-sizing: border-box;
			background-color: #f0f0f4;
			padding: 70px 20px 20px 20px;
			&.mobileDisplayCondition {
				display: flex;
				flex-direction: column;
			}
		}

		.goBack {
			width: 30px;
			height: 27px;
			position: absolute;
			top: 23.2px;
			right: 20px;
		}
	}
	@include DesktopSize {
		.container {
			#search-item {
				width: calc(50% - 15.5px);
				font-size: 1.25rem;
				height: 40px;
			}

			.mobileHide {
				display: block;
			}
			.list-detail-wrapper {
				flex-basis: 100%;
				width: 100%;
				display: flex;
				gap: 20px;
				.list {
					flex-basis: calc(100% * (952 / 1594));
					flex-grow: 1;
				}
			}
			.detail {
				padding: 0;
				display: flex;
				flex-direction: column;
				position: static;
				min-height: 0;
				flex-basis: calc(100% * (642 / 1594));
				max-width: 642px;
				max-height: none;
				/* temp debuging point */
			}
			.goBack {
				display: none;
			}
		}
	}
</style>
